import { Switch } from '@thanx/uikit/switch'
import truncate from 'lodash/truncate'
import { useMemo, useState } from 'react'
import { Button } from 'react-bootstrap'
import { useStyletron } from 'styletron-react'

import { buildTranslate } from 'locales'
import type { Fields as Modifier, Item } from 'models/Modifier'
import { currencyFormatter } from 'utilities/formatters'
import ItemsModal from './ItemsModal'
import placeholder from './placeholder.svg'

const t = buildTranslate('redeem_manager.modifier_select')

const MAXIMUM_MODIFIER_NAME_CHARACTERS = 75

type ModifierListItemProps = {
  modifier: Modifier
  canDelete?: boolean
  onDelete?: () => void
  isViewMode?: boolean
  productIds?: string[]
}

const ModifierListItem = ({
  modifier,
  canDelete = false,
  onDelete,
  productIds = [],
  isViewMode = false,
}: ModifierListItemProps) => {
  const [isModalOpen, setModalOpen] = useState(false)
  const { image = { default: null } } = modifier?.option || {}
  const [css] = useStyletron()
  const displayDeleteButton =
    Boolean(canDelete) && Boolean(onDelete) && !isViewMode

  const { name = '', price } = modifier?.option || {}

  const getFilteredSortedItemNames = (
    items: Item[],
    productIds: string[]
  ): string[] =>
    items
      .filter(item => productIds.includes(item.chain_uid))
      .map(item => item.name)
      .sort((a, b) => a.localeCompare(b))

  const buildOptionName = (baseName: string, itemNames: string[]): string =>
    `${baseName} - ${itemNames.join(', ')}`

  const getOptionDisplayName = ({
    fullName,
    shouldTruncate,
  }: {
    fullName: string
    shouldTruncate: boolean
  }): string =>
    !shouldTruncate
      ? fullName
      : truncate(fullName, { length: MAXIMUM_MODIFIER_NAME_CHARACTERS })

  const getHiddenItemCount = (
    allNames: string[],
    displayString: string
  ): number => allNames.filter(name => !displayString.includes(name)).length

  const { optionNameWithItems, isTruncated, hiddenItemCount } = useMemo(() => {
    const hasSelectedProducts = productIds.length > 0
    const filteredNames = getFilteredSortedItemNames(modifier.items, productIds)

    const fullName = buildOptionName(name, filteredNames)
    const shouldTruncate = hasSelectedProducts
    const exceedsLength = fullName.length > MAXIMUM_MODIFIER_NAME_CHARACTERS

    const isTruncated = shouldTruncate && exceedsLength
    const updatedDisplayName = getOptionDisplayName({
      fullName,
      shouldTruncate: isTruncated,
    })
    const optionNameWithItems = hasSelectedProducts ? updatedDisplayName : name

    const hiddenItemCount = getHiddenItemCount(
      filteredNames,
      optionNameWithItems
    )

    return {
      optionNameWithItems,
      isTruncated,
      hiddenItemCount,
    }
  }, [modifier.items, productIds, name])

  return (
    <>
      <div className="d-flex align-items-center justify-content-between border-1 grey-30-border border-radius-5 grey-05-bkg p-xs mb-xs">
        <div className="flex-1 d-flex mb-0 align-items-center">
          <img
            alt={name || t('image_alt')}
            className={`${css({
              width: '32px',
              height: '32px',
            })} mr-xs`}
            src={image?.default || placeholder}
          />

          <p
            className={`mb-0 body-text-4 grey-70 ${css({
              wordWrap: 'break-word',
            })}`}
          >
            {optionNameWithItems}
          </p>

          <Switch condition={Boolean(isTruncated)}>
            <Button
              bsStyle="link"
              className={`${css({
                minWidth: '75px',
              })} m-0 mr-s ml-xs p-0`}
              onClick={() => setModalOpen(true)}
            >
              {t('show_count_more', { count: hiddenItemCount })}
            </Button>
          </Switch>
        </div>

        <Switch condition={price > 0}>
          <p className="mr-l mb-0 body-text-4 grey-70">
            {currencyFormatter(price)}
          </p>
        </Switch>

        <Switch condition={displayDeleteButton}>
          <button
            type="button"
            title={t('delete')}
            aria-label={t('delete')}
            className="font-size-24 grey-70 light cursor-pointer border-0 bg-transparent p-0"
            onClick={onDelete}
          >
            &times;
          </button>
        </Switch>
      </div>

      <ItemsModal
        isOpen={isModalOpen}
        onClose={() => setModalOpen(false)}
        title={name}
        items={getFilteredSortedItemNames(modifier.items, productIds)}
      />
    </>
  )
}

export default ModifierListItem
